import { useEffect, useState, type CSSProperties } from "react";
import { useIntl } from "react-intl";
import { eventsOnDay, layoutCalendarDay, localCalendarDate, type CalendarBlock } from "@/lib/microsoft-calendar-time";

interface Props {
  days: string[]; events: CalendarBlock[]; start: string; end: string;
  overlaps: (event: CalendarBlock) => boolean;
}
const HOUR_HEIGHT = 64;
const TRACKS = "56px repeat(7, minmax(100px, 1fr))";

export function MicrosoftCalendarWeek({ days, events, start, end, overlaps }: Props) {
  const intl = useIntl(); const t = (id: string) => intl.formatMessage({ id: `microsoft.agenda.${id}` });
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const timer = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(timer); }, []);
  const validDraft = Number.isFinite(new Date(start).getTime()) && new Date(end).getTime() > new Date(start).getTime();
  const entries: CalendarBlock[] = validDraft ? [...events, { id: "smycles-draft", subject: t("draft"), start, end,
    allDay: false, showAs: "busy", privateEvent: false, webUrl: "", draft: true }] : events;
  const layout = days.map(day => layoutCalendarDay(entries, day));
  const allDay = days.map(day => eventsOnDay(events, day).filter(event => event.allDay));
  const firstHour = Math.min(8, ...layout.flat().map(item => Math.floor(item.from / 60)));
  const lastHour = Math.max(18, ...layout.flat().map(item => Math.ceil(item.to / 60)));
  const hours = Array.from({ length: lastHour - firstHour }, (_, index) => firstHour + index);
  const height = (lastHour - firstHour) * HOUR_HEIGHT;
  const currentDay = localCalendarDate(now);
  const currentMinute = now.getHours() * 60 + now.getMinutes();
  const statuses = new Set(["free", "tentative", "busy", "oof", "workingElsewhere"]);
  function title(event: CalendarBlock) { return event.privateEvent ? t("private") : event.subject || t("untitled"); }
  function renderEvent(event: CalendarBlock, style?: CSSProperties) {
    const conflict = !event.draft && overlaps(event);
    const timing = event.allDay ? t("allDay") : `${intl.formatTime(event.start)} – ${intl.formatTime(event.end)}`;
    const label = [title(event), timing, !event.draft ? t(statuses.has(event.showAs) ? event.showAs : "unknown") : "",
      conflict ? t("overlap") : ""].filter(Boolean).join(" · ");
    const className = `${style ? "absolute" : "relative block"} overflow-hidden rounded border px-1.5 py-0.5 text-smycles-meta leading-tight
      ${event.draft ? "border-dashed border-ink-ui bg-surface text-ink" : event.showAs === "free" || event.showAs === "workingElsewhere"
        ? "border-ink/15 bg-ink/[0.06] text-ink" : "border-transparent bg-ink text-ink"}
      ${conflict ? "ring-2 ring-ink-attention" : ""}
      ${event.webUrl ? "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-ui" : ""}`;
    const tall = !style || Number(style.height) >= 48;
    const showTime = !style || Number(style.height) >= 40;
    const content = <><span className={`block font-medium ${tall ? "line-clamp-2" : "truncate"}`}>{conflict ? "! " : ""}{title(event)}</span>
      {!event.allDay && showTime ? <span className="block truncate">{timing}</span> : null}</>;
    return event.webUrl ? <a key={event.id} href={event.webUrl} target="_blank" rel="noopener noreferrer"
      className={className} style={style} aria-label={`${label} · ${t("outlook")}`} title={label}>{content}</a>
      : <div key={event.id} className={className} style={style} aria-label={label} title={label}>{content}</div>;
  }
  return <div className="isolate max-h-[720px] overflow-auto rounded border border-ink/15" role="region"
    tabIndex={0} aria-label={t("weekView")}>
    <div style={{ minWidth: 756 }}>
      <div className="sticky top-0 z-[var(--z-raised)] bg-surface">
        <div className="grid border-b border-ink/10" style={{ gridTemplateColumns: TRACKS }}>
          <div className="p-2 text-smycles-micro text-ink-muted" />
          {days.map(day => <div key={day} aria-current={day === currentDay ? "date" : undefined}
            className={`border-l border-ink/10 px-2 py-3 ${day === currentDay ? "bg-ink/[0.06]" : ""}`}>
            <p className="text-smycles-meta text-ink-muted">{intl.formatDate(`${day}T12:00`, { weekday: "short" })}</p>
            <p className={`mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-smycles-title ${day === currentDay ? "bg-ink text-ink" : ""}`}>
              {intl.formatDate(`${day}T12:00`, { day: "numeric" })}
            </p>
          </div>)}
        </div>
        {allDay.some(events => events.length) ? <div className="grid border-b border-ink/10" style={{ gridTemplateColumns: TRACKS }}>
          <span className="p-1 text-smycles-micro text-ink-muted">{t("allDay")}</span>
          {allDay.map((events, index) => <div key={days[index]} className="space-y-1 border-l border-ink/10 p-1">
            {events.map(event => renderEvent(event))}
          </div>)}
        </div> : null}
      </div>
      <div className="grid" style={{ gridTemplateColumns: TRACKS }}>
        <div className="relative bg-surface" style={{ height }}>
          {hours.map(hour => <span key={hour} className="absolute right-2 text-smycles-micro text-ink-muted"
            style={{ top: (hour - firstHour) * HOUR_HEIGHT + 2 }}>{String(hour).padStart(2, "0")}:00</span>)}
        </div>
        {days.map((day, index) => <div key={day} className={`relative border-l border-ink/10 ${index > 4 ? "bg-ink/[0.02]" : ""}`} style={{ height }}
          aria-label={intl.formatDate(`${day}T12:00`, { dateStyle: "full" })}>
          {hours.map(hour => <div key={hour} className="pointer-events-none absolute inset-x-0 border-t border-ink/10"
            style={{ top: (hour - firstHour) * HOUR_HEIGHT, height: HOUR_HEIGHT }}>
            <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-ink/10" />
          </div>)}
          {layout[index]?.map(({ event, from, to, column, columns }) => renderEvent(event, {
            top: (from / 60 - firstHour) * HOUR_HEIGHT,
            height: (to - from) / 60 * HOUR_HEIGHT,
            left: `calc(${column / columns * 100}% + 2px)`, width: `calc(${100 / columns}% - 4px)`,
          }))}
          {day === currentDay && currentMinute >= firstHour * 60 && currentMinute < lastHour * 60
            ? <div className="pointer-events-none absolute inset-x-0 border-t-2 border-ink-attention"
              style={{ top: (currentMinute / 60 - firstHour) * HOUR_HEIGHT }} aria-label={t("now")}>
              <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-ink-attention" />
            </div> : null}
        </div>)}
      </div>
    </div>
  </div>;
}
